import { motion } from "framer-motion";
import { ArrowUpRight, Check, Boxes, MapPinCheck, ShoppingCart, Eye, TrendingUp } from "lucide-react";

const ICONS = { Boxes, MapPinCheck, ShoppingCart, Eye, TrendingUp };

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const ProductRow = ({ p, index, flip }) => {
  const Icon = ICONS[p.icon] || Boxes;

  return (
    <article
      data-testid={`product-card-${p.slug}`}
      className="grid items-center gap-10 border-t border-slate-200 py-16 first:border-t-0 lg:grid-cols-2 lg:gap-16"
    >
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        className={flip ? "lg:order-2" : ""}
      >
        <div className="group relative overflow-hidden rounded-3xl shadow-[0_25px_60px_-25px_rgba(15,23,42,0.35)]">
          <img
            src={p.image_url}
            alt={p.title}
            loading="lazy"
            className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-80 lg:h-[380px]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/70 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 inline-flex items-center gap-2.5 rounded-xl border border-white/20 bg-white/90 px-4 py-2.5 backdrop-blur-md">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-[#0F172A]">
              <Icon className="h-4 w-4 text-[#F59E0B]" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#0F172A]">{p.title}</span>
              <span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-600">
                Live
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        className={flip ? "lg:order-1" : ""}
      >
        <div className="flex items-center gap-4">
          <span className="font-mono text-sm font-semibold text-[#F59E0B]">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="rounded-full border border-slate-200 bg-white px-3.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {p.badge}
          </span>
        </div>

        <h3 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-4xl">
          {p.title}
        </h3>
        <p className="mt-1.5 font-display text-base font-semibold text-slate-500">{p.subtitle}</p>

        <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">{p.description}</p>

        <ul className="mt-7 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {p.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-amber-100">
                <Check className="h-3 w-3 text-amber-600" />
              </span>
              {f}
            </li>
          ))}
        </ul>

        <a
          data-testid={`visit-product-btn-${p.slug}`}
          href={p.link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-9 inline-flex items-center gap-2 rounded-md bg-[#0F172A] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#F59E0B] hover:text-[#0F172A] hover:shadow-lg"
        >
          Visit Product
          <ArrowUpRight className="h-4 w-4" />
        </a>
      </motion.div>
    </article>
  );
};

export const ProductsSection = ({ products }) => (
  <section id="products" data-testid="product-portfolio-section" className="mx-auto max-w-7xl px-6 py-24">
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      className="max-w-2xl"
    >
      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F59E0B]">
        The Portfolio
      </span>
      <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-4xl">
        Five systems. One partner.
      </h2>
      <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
        Every Smart-In product is live in production today — pick the one that fits your
        operation, or combine them into a single stack.
      </p>
    </motion.div>

    <div className="mt-6">
      {products.map((p, i) => (
        <ProductRow key={p.slug} p={p} index={i} flip={i % 2 === 1} />
      ))}
    </div>
  </section>
);
